import { Chip } from "@gigpro/ui/Chip";
import { CheckCircle, ErrorSign, Hourglass, Warning } from "@gigpro/ui/Icons";
import { Skeleton } from "@gigpro/ui/Skeleton";
import { useQuery } from "@tanstack/react-query";
import type { OnboardingStep } from "api/queries/pros";
import React from "react";
import { onboardingStepsQuery } from "./queries";

type OnboardingStepsProps = { proId: number };

const requirementLabelLookup: Record<OnboardingStep["requirement_name"], string> = {
  PROFILE: "Complete Profile",
  STRIPE: "Setup Stripe",
  INSURANCE: "Complete Insurance",
  INTERVIEW: "Schedule Interview",
  W2: "W2 Onboarding",
  BACKGROUND_CHECK: "Background Check",
};

// The W2 step stays open after Zeal until E-Verify closes authorized — name which half is holding it up.
const w2StatusLabelLookup: Partial<Record<OnboardingStep["status"], string>> = {
  AWAITING_VERIFICATION: "Awaiting E-Verify",
  FAILED: "E-Verify failed",
};

const stepLabel = (step: OnboardingStep) =>
  (step.requirement_name === "W2" && w2StatusLabelLookup[step.status]) || requirementLabelLookup[step.requirement_name];

export function OnboardingSteps({ proId }: Readonly<OnboardingStepsProps>) {
  const onboardingSteps = useQuery(onboardingStepsQuery({ proId }));

  if (onboardingSteps.isLoading) return <Skeleton className="h-9" />;
  if (!onboardingSteps.data || onboardingSteps.data.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1">
      {onboardingSteps.data.map((step, index) => {
        let indicator: React.ReactNode;
        if (step.status === "COMPLETED") {
          indicator = <CheckCircle className="size-3.5 shrink-0 text-success" />;
        } else if (step.status === "FAILED") {
          indicator = <ErrorSign className="size-3.5 shrink-0 text-error" />;
        } else if (step.status === "AWAITING_VERIFICATION") {
          indicator = <Hourglass className="size-3.5 shrink-0 text-success" />;
        } else if (step.status === "EXPIRED") {
          indicator = <Warning className="size-3.5 shrink-0 text-amber" />;
        } else {
          indicator = (
            <Chip className="size-3.5 border border-gray bg-transparent p-0 group-data-[status=ONGOING]:bg-success group-data-[status=ONGOING]:text-white">
              {index + 1}
            </Chip>
          );
        }
        return (
          <div key={step.requirement_name} className="group flex flex-1 flex-col gap-1.5" data-status={step.status}>
            <div className="h-1 rounded-full bg-success group-data-[status=EXPIRED]:bg-amber group-data-[status=FAILED]:bg-error group-data-[status=INCOMPLETE]:bg-light-gray" />

            <p className="flex gap-1 text-small font-semibold text-gray group-data-[status=AWAITING_VERIFICATION]:text-black group-data-[status=EXPIRED]:text-amber group-data-[status=FAILED]:text-error group-data-[status=ONGOING]:text-black">
              {indicator}
              {stepLabel(step)}
            </p>
          </div>
        );
      })}
    </div>
  );
}
